import { useState, useEffect } from "react";
import axios from "axios";
import { Award, BookOpen, Briefcase, GraduationCap, Lightbulb, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUBJECT_ICONS = {
  English: "📖",
  Math: "🧮",
  Science: "🔬",
  Health: "❤️‍🩹",
};

/**
 * CTEProfileReport
 * Renders a student's Lesson 1 CTE Profile Report (frontend view of /api/cte/me/report).
 * Designed to be embedded on the Middle School Dashboard once Lesson 1 is complete.
 */
function CTEProfileReport({ studentId, compact = false }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const load = async () => {
    try {
      const path = studentId ? `/cte/student/${studentId}/report` : `/cte/me/report`;
      const res = await axios.get(`${API}${path}`);
      setReport(res.data);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;
  if (!report) return null;
  // Only render if student actually engaged with Lesson 1
  if (!report.profile && report.engagement?.interactions_completed === 0) return null;

  const profile = report.profile;
  const skills = report.top_skills || [];
  const pathways = report.career_pathways || [];
  const subjects = report.academic_credit_subjects || [];

  return (
    <div
      className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8 border-2 border-purple-100"
      data-testid="cte-profile-report"
    >
      <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 px-6 py-4 text-white">
        <div className="flex items-center gap-2">
          <Award className="w-5 h-5" />
          <h2 className="font-bold text-lg">My CTE Profile Report</h2>
          {report.completed && (
            <span className="ml-auto bg-white/20 px-2 py-0.5 rounded-full text-xs font-semibold">
              Lesson 1 Complete ✓
            </span>
          )}
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Profile + Idea Summary */}
        {profile && (
          <div className="flex items-start gap-4 flex-wrap" data-testid="cte-profile-summary">
            <div className="text-6xl">{profile.icon}</div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wide text-purple-600 font-bold mb-1">
                Entrepreneur Profile
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{profile.name}</h3>
              <div className="text-sm text-gray-600 mt-1">⚡ {profile.superpower}</div>
            </div>
            {report.business_idea?.name && (
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-3 max-w-xs">
                <div className="text-xs font-bold text-orange-600 uppercase tracking-wide mb-1">
                  My Business
                </div>
                <div className="text-lg font-bold text-gray-900">
                  {report.business_idea.name}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Identified Problem + Idea Theme */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-pink-50 border border-pink-100 rounded-xl p-4">
            <div className="flex items-center gap-1 text-pink-700 font-semibold text-sm mb-1">
              <Lightbulb className="w-4 h-4" /> Identified Problem
            </div>
            <div className="text-gray-800 font-medium" data-testid="cte-identified-problem">
              {report.identified_problem}
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
            <div className="flex items-center gap-1 text-blue-700 font-semibold text-sm mb-1">
              <Sparkles className="w-4 h-4" /> Business Idea Theme
            </div>
            <div className="text-gray-800 font-medium" data-testid="cte-idea-category">
              {report.idea_category}
            </div>
          </div>
        </div>

        {/* Top 3 Strengths (with dual standards) */}
        {skills.length > 0 && (
          <div data-testid="cte-top-skills">
            <div className="flex items-center gap-1 mb-2">
              <Award className="w-4 h-4 text-purple-600" />
              <h4 className="font-bold text-gray-800">Top Strength Indicators</h4>
            </div>
            <div className="space-y-2">
              {skills.map((s) => (
                <div
                  key={s.id}
                  className="bg-gray-50 border border-gray-200 rounded-xl p-3"
                  data-testid={`cte-skill-${s.id}`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="font-semibold text-gray-900">{s.title}</div>
                    <span className="text-xs font-mono bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                      {s.id}
                    </span>
                  </div>
                  {!compact && (
                    <div className="mt-2 grid sm:grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="font-semibold text-orange-700">Nevada CTE:</span>{" "}
                        <span className="text-gray-600">{s.nevada_cte}</span>
                      </div>
                      <div>
                        <span className="font-semibold text-blue-700">CCTC:</span>{" "}
                        <span className="text-gray-600">{s.cctc}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Career Pathways */}
        {pathways.length > 0 && (
          <div data-testid="cte-career-pathways">
            <div className="flex items-center gap-1 mb-2">
              <Briefcase className="w-4 h-4 text-green-600" />
              <h4 className="font-bold text-gray-800">Career Pathway Suggestions</h4>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {pathways.map((p) => (
                <div
                  key={p.key}
                  className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-100 rounded-xl p-3"
                  data-testid={`cte-pathway-${p.key}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">{p.icon}</span>
                    <div className="font-bold text-gray-900 text-sm">{p.name}</div>
                  </div>
                  {!compact && (
                    <div className="text-xs text-gray-600">{p.description}</div>
                  )}
                  {p.in_demand && (
                    <span className="inline-block mt-1 text-[10px] font-bold bg-green-200 text-green-800 px-2 py-0.5 rounded-full">
                      ⭐ IN-DEMAND
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Academic Credit */}
        {subjects.length > 0 && (
          <div data-testid="cte-academic-credit">
            <div className="flex items-center gap-1 mb-2">
              <GraduationCap className="w-4 h-4 text-indigo-600" />
              <h4 className="font-bold text-gray-800">Academic Credit Cross-Map</h4>
            </div>
            <p className="text-xs text-gray-600 mb-2">
              This work supports learning in these subjects:
            </p>
            <div className="flex flex-wrap gap-2">
              {subjects.map((subj) => (
                <span
                  key={subj}
                  className="inline-flex items-center gap-1 bg-indigo-50 border border-indigo-200 text-indigo-800 text-sm font-semibold px-3 py-1 rounded-full"
                  data-testid={`cte-subject-${subj}`}
                >
                  <span>{SUBJECT_ICONS[subj] || "📚"}</span> {subj}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Engagement footer */}
        <div className="border-t border-gray-100 pt-3 flex items-center gap-3 text-xs text-gray-500 flex-wrap">
          <BookOpen className="w-3.5 h-3.5" />
          <span data-testid="cte-engagement-count">
            {report.engagement?.interactions_completed || 0} interactions completed
          </span>
          <span>•</span>
          <span>{report.engagement?.spark_questions_answered || 0}/4 quiz</span>
          <span>•</span>
          <span>{report.engagement?.activity_steps_completed || 0}/3 activity</span>
          <span>•</span>
          <span>Builder: {report.engagement?.builder_complete ? "✓" : "—"}</span>
        </div>
      </div>
    </div>
  );
}

export default CTEProfileReport;
